import { AxiosInstance } from 'axios'
import useSWR from 'swr'
import { z } from 'zod'

import {
  recipeScheduleSchema,
  candidateSchema,
  positionSchema,
  companySchema,
  contactSchema,
  funnelSchema,
  tolerantTaskSchema,
  Candidate,
  Contact,
  Funnel,
  Task,
  RecipeSchedule,
  Position,
  Company,
} from '../types'
import { validateResponse } from '../utils'
import { useApi } from '../store/mainContext'
import usePowers, { UsePowersReturn } from './usePowers'

export type CandidateFunnelCard = Candidate & {
  tasks: Task[]
  schedules: RecipeSchedule[]
  position?: Position
  hiringManager?: Contact
  company?: Company
}

export type ContactFunnelCard = Contact & {
  tasks: Task[]
  schedules: RecipeSchedule[]
  company?: Company
}

export type FunnelCard = CandidateFunnelCard | ContactFunnelCard

export type FunnelWithCards = {
  funnel: Funnel
  cards: FunnelCard[]
}

export async function loadFunnelData(
  api: AxiosInstance,
  powers: UsePowersReturn,
  funnelName: string,
): Promise<FunnelWithCards | undefined> {
  const funnels = await api
    .get('funnels', { params: { name: funnelName } })
    .then(validateResponse(z.array(funnelSchema)))

  const funnel = funnels.find((candidate) => candidate.name === funnelName)

  if (!funnel) return

  const collection = funnel.collection

  const cardsResponse = await api.get(`${collection}/in`, {
    params: { status: funnel.columns.map((column) => column.id) },
    headers: { 'astor-sortby': 'lastUpdateOn', 'astor-sortby-dir': '1' },
  })

  const cards =
    collection === 'candidates'
      ? validateResponse(z.array(candidateSchema))(cardsResponse)
      : validateResponse(z.array(contactSchema))(cardsResponse)

  const cardIds = cards.map((c) => c._id)

  const [tasks, schedules, candidatePositions, contactCompanies] =
    await Promise.all([
      api
        .get(`tasks/in`, {
          params: { relatedId: cardIds },
          headers: { 'astor-sortby': 'dueDate', 'astor-sortby-dir': '-1' },
        })
        .then(validateResponse(z.array(tolerantTaskSchema))),
      powers.hasAll(['run-and-schedule-recipes'])
        ? api
            .get('recipe-schedules/in', {
              headers: {
                'astor-sortby': 'whenToRun',
                'astor-sortby-dir': '-1',
              },
              params: { runOnId: cardIds, status: 'pending' },
            })
            .then(validateResponse(z.array(recipeScheduleSchema)))
        : [],
      collection === 'candidates'
        ? api
            .get('/positions/in', {
              params: { _id: cards.map((c) => (c as Candidate).positionId) },
            })
            .then(validateResponse(z.array(positionSchema)))
        : [],
      collection === 'contacts'
        ? api
            .get('/companies/in', {
              params: { _id: cards.map((c) => (c as Contact).companyId) },
            })
            .then(validateResponse(z.array(companySchema)))
        : [],
    ])

  const [positionCompanies, positionHiringManagers] = await Promise.all([
    collection === 'candidates'
      ? api
          .get('/companies/in', {
            params: { _id: candidatePositions.map((p) => p.companyId) },
          })
          .then(validateResponse(z.array(companySchema)))
      : [],
    collection === 'candidates'
      ? api
          .get('/contacts/in', {
            params: { _id: candidatePositions.map((p) => p.hiringManagerId) },
          })
          .then(validateResponse(z.array(contactSchema)))
      : [],
  ])

  const enrichedCards: FunnelCard[] = cards.map((card) => {
    const base = {
      schedules: schedules.filter((s) => s.runOnId === card._id),
      tasks: tasks.filter((t) => t.relatedId === card._id && !t.isDone),
    }

    if (collection === 'candidates') {
      const candidate = card as Candidate
      const position = candidatePositions.find(
        (c) => c._id === candidate.positionId,
      )
      return {
        ...candidate,
        ...base,
        position,
        hiringManager: positionHiringManagers.find(
          (c) => c._id === position?.hiringManagerId,
        ),
        company: positionCompanies.find((c) => c._id === position?.companyId),
      } as CandidateFunnelCard
    }

    const contact = card as Contact
    return {
      ...contact,
      ...base,
      company: contactCompanies.find((c) => c._id === contact.companyId),
    } as ContactFunnelCard
  })

  return {
    funnel,
    cards: enrichedCards,
  }
}

export default function useFunnel(funnelName: string) {
  const powers = usePowers()
  const api = useApi()

  return useSWR(['funnel', funnelName], () =>
    loadFunnelData(api, powers, funnelName),
  )
}
